<script setup lang="ts">
import { useListAdminAlgorithmVersions } from '@swipe4work/api-orval'
import type { ListAdminAlgorithmVersionsAlgorithmVersionListItemDto } from '@swipe4work/models-orval'

definePage({
  path: '/matching/algorithm-versions',
  name: '/matching/AlgorithmVersions',
  meta: {
    title: 'Algoritme',
    permission: {
      resource: 'MatchingAlgorithm',
      level: 'Read',
    },
  },
})

const router = useRouter()

const { data, isPending, isError } = useListAdminAlgorithmVersions({ query: { staleTime: 5 * 60 * 1000 } })
const algorithmVersions = computed(() => data.value?.algorithmVersions ?? [])

function goToVersion(version: ListAdminAlgorithmVersionsAlgorithmVersionListItemDto) {
  router.push({
    name: '/matching/AlgorithmVersionDetail',
    params: { id: String(version.id) },
  })
}
</script>

<template>
  <div class="space-y-5">
    <Card>
      <template #content>
        <div class="flex flex-col gap-4">
          <h3 class="text-lg font-semibold">
            Algoritmeversies
          </h3>

          <div v-if="isPending" class="flex flex-col gap-2">
            <Skeleton v-for="n in 5" :key="n" height="3rem" />
          </div>
          <Message v-else-if="isError" severity="error">
            Er is iets misgegaan bij het laden van algoritmeversies.
          </Message>
          <DataTable
            v-else
            :value="algorithmVersions"
            class="w-full"
            row-hover
            @row-click="(e) => goToVersion(e.data)"
          >
            <Column field="groupName" header="Groep" style="width: 8rem">
              <template #body="{ data: version }">
                <span v-if="version.groupName" class="text-sm text-primary-600 font-mono">{{ version.groupName }}</span>
                <span v-else class="text-xs text-surface-400">—</span>
              </template>
            </Column>
            <Column field="version" header="Versie" />
            <Column field="description" header="Omschrijving">
              <template #body="{ data: version }">
                <span class="text-sm text-surface-600">{{ version.description || '—' }}</span>
              </template>
            </Column>
            <Column field="isActive" header="Actief" style="width: 6rem">
              <template #body="{ data: version }">
                <PillTag
                  :value="version.isActive ? 'Actief' : 'Inactief'"
                  :severity="version.isActive ? 'success' : 'secondary'"
                />
              </template>
            </Column>
            <Column field="forLeads" header="Leads" style="width: 6rem">
              <template #body="{ data: version }">
                <i
                  :class="version.forLeads ? 'fa-regular fa-check text-green-500' : 'fa-regular fa-xmark text-surface-400'"
                />
              </template>
            </Column>
            <Column field="forSimple" header="Simpel" style="width: 6rem">
              <template #body="{ data: version }">
                <i v-if="version.forSimple === null" class="fa-regular fa-minus text-surface-400" />
                <i v-else-if="version.forSimple" class="fa-regular fa-check text-green-500" />
                <i v-else class="fa-regular fa-xmark text-surface-400" />
              </template>
            </Column>
            <Column field="visibilityThreshold" header="Drempelwaarde" style="width: 9rem">
              <template #body="{ data: version }">
                <span class="text-sm font-mono">{{ version.visibilityThreshold }}</span>
              </template>
            </Column>
            <Column style="width: 4rem">
              <template #body>
                <i class="fa-regular fa-chevron-right text-surface-400" />
              </template>
            </Column>
          </DataTable>
        </div>
      </template>
    </Card>
  </div>
</template>
